import { useSuperMutation, useSuperQuery } from '@vadiun/react-hooks-legacy';
import useEventoService, {
  EventReferentPayload,
  UpdateEventPayload
} from '../services/useEventoService';

export interface FileMutationInput {
  file?: File;
  name: string;
  observacion?: string;
  existingUrl?: string;
}

export const useEventoDetail = (eventId: number) => {
  const eventoService = useEventoService();

  const eventQuery = useSuperQuery(
    () => eventoService.getEventById(eventId),
    { showSpinner: true },
    [eventId]
  );

  // Ninguna mutación de sub-recurso confía en su propia respuesta para
  // actualizar la vista — se vio en la práctica que distintos endpoints de
  // escritura no siempre incluyen todas las relaciones (`observations`,
  // por ejemplo) en lo que devuelven. Ante cualquier mutación exitosa se
  // refetchea el evento completo con `getEventById` (GET /events/:id, que
  // sí las incluye siempre), en vez de reemplazar el estado local con la
  // respuesta de la escritura.
  const reloadOnSuccess = (state: { isError: boolean }) => {
    if (!state.isError) {
      eventQuery.reload();
    }
  };

  const updateGeneralDataMutation = useSuperMutation(
    (payload: UpdateEventPayload) =>
      eventoService.updateEvent(eventId, payload),
    {
      showSpinner: true,
      messages: { success: { message: 'Evento actualizado con éxito' } },
      onMutationFinish: reloadOnSuccess
    }
  );

  const addReferentMutation = useSuperMutation(
    (payload: EventReferentPayload) =>
      eventoService.addReferent(eventId, payload),
    {
      showSpinner: true,
      messages: { success: { message: 'Referente agregado' } },
      onMutationFinish: reloadOnSuccess
    }
  );

  const updateReferentMutation = useSuperMutation(
    ({
      referentId,
      payload
    }: {
      referentId: number;
      payload: EventReferentPayload;
    }) => eventoService.updateReferent(eventId, referentId, payload),
    {
      showSpinner: true,
      messages: { success: { message: 'Referente actualizado' } },
      onMutationFinish: reloadOnSuccess
    }
  );

  const removeReferentMutation = useSuperMutation(
    (referentId: number) => eventoService.removeReferent(eventId, referentId),
    {
      showSpinner: true,
      messages: { success: { message: 'Referente eliminado' } },
      onMutationFinish: reloadOnSuccess
    }
  );

  const addObservationMutation = useSuperMutation(
    (observation: string) => eventoService.addObservation(eventId, observation),
    {
      showSpinner: true,
      messages: { success: { message: 'Observación agregada' } },
      onMutationFinish: reloadOnSuccess
    }
  );

  // Señaléticas y presupuestos comparten el mismo flujo de dos pasos: subir
  // el archivo a S3 (`uploadFile`) y recién después persistir la url en el
  // evento (`addSignage`/`addBudget`). Se encadenan dentro de una sola
  // mutación para que el usuario vea un único spinner, no dos pasos.
  const addSignageMutation = useSuperMutation(
    async ({ file, name, observacion }: FileMutationInput) => {
      const url = await eventoService.uploadFile(eventId, file as File, 'signage');
      await eventoService.addSignage(eventId, { name, observacion, url });
    },
    {
      showSpinner: true,
      messages: { success: { message: 'Señalética agregada' } },
      onMutationFinish: reloadOnSuccess
    }
  );

  const updateSignageMutation = useSuperMutation(
    async ({
      signageId,
      file,
      name,
      observacion,
      existingUrl
    }: FileMutationInput & { signageId: number }) => {
      const url = file
        ? await eventoService.uploadFile(eventId, file, 'signage')
        : existingUrl;
      await eventoService.updateSignage(eventId, signageId, {
        name,
        observacion,
        url
      });
    },
    {
      showSpinner: true,
      messages: { success: { message: 'Señalética actualizada' } },
      onMutationFinish: reloadOnSuccess
    }
  );

  const removeSignageMutation = useSuperMutation(
    (signageId: number) => eventoService.removeSignage(eventId, signageId),
    {
      showSpinner: true,
      messages: { success: { message: 'Señalética eliminada' } },
      onMutationFinish: reloadOnSuccess
    }
  );

  const addBudgetMutation = useSuperMutation(
    async ({ file, name, observacion }: FileMutationInput) => {
      const url = await eventoService.uploadFile(eventId, file as File, 'budget');
      await eventoService.addBudget(eventId, { name, observacion, url });
    },
    {
      showSpinner: true,
      messages: { success: { message: 'Presupuesto agregado' } },
      onMutationFinish: reloadOnSuccess
    }
  );

  const updateBudgetMutation = useSuperMutation(
    async ({
      budgetId,
      file,
      name,
      observacion,
      existingUrl
    }: FileMutationInput & { budgetId: number }) => {
      const url = file
        ? await eventoService.uploadFile(eventId, file, 'budget')
        : existingUrl;
      await eventoService.updateBudget(eventId, budgetId, {
        name,
        observacion,
        url
      });
    },
    {
      showSpinner: true,
      messages: { success: { message: 'Presupuesto actualizado' } },
      onMutationFinish: reloadOnSuccess
    }
  );

  const removeBudgetMutation = useSuperMutation(
    (budgetId: number) => eventoService.removeBudget(eventId, budgetId),
    {
      showSpinner: true,
      messages: { success: { message: 'Presupuesto eliminado' } },
      onMutationFinish: reloadOnSuccess
    }
  );

  return {
    event: eventQuery.data ?? null,
    isLoading: eventQuery.isLoading,
    updateGeneralData: updateGeneralDataMutation.mutate,
    isUpdatingGeneralData: updateGeneralDataMutation.isLoading,
    addReferent: addReferentMutation.mutate,
    isAddingReferent: addReferentMutation.isLoading,
    updateReferent: (referentId: number, payload: EventReferentPayload) =>
      updateReferentMutation.mutate({ referentId, payload }),
    isUpdatingReferent: updateReferentMutation.isLoading,
    removeReferent: removeReferentMutation.mutate,
    addObservation: addObservationMutation.mutate,
    isAddingObservation: addObservationMutation.isLoading,
    addSignage: addSignageMutation.mutate,
    isAddingSignage: addSignageMutation.isLoading,
    updateSignage: (signageId: number, payload: FileMutationInput) =>
      updateSignageMutation.mutate({ signageId, ...payload }),
    isUpdatingSignage: updateSignageMutation.isLoading,
    removeSignage: removeSignageMutation.mutate,
    addBudget: addBudgetMutation.mutate,
    isAddingBudget: addBudgetMutation.isLoading,
    updateBudget: (budgetId: number, payload: FileMutationInput) =>
      updateBudgetMutation.mutate({ budgetId, ...payload }),
    isUpdatingBudget: updateBudgetMutation.isLoading,
    removeBudget: removeBudgetMutation.mutate
  };
};

export default useEventoDetail;
